import { useSnackbar } from 'notistack';
import { PATH_DASHBOARD } from 'src/routes/paths';
import useAuth from './useAuth';
import useInvites from './useInvites';
import { useTranslation } from 'react-i18next';
import useAcceptedFoodTypes from './useAcceptedFoodTypes';
import useConnectOnboardingStatus from './useConnectOnboardingStatus';
import _isEmpty from 'lodash/isEmpty';
import useEventBus from './useEventBus';
import { parseJsonObject } from 'src/utils/json';
import { isCanadaCountry, shouldShowOrgEinTask } from 'src/utils/ein';

/**
 * @typedef Prereq
 * @property {string} displayName Name of the action required
 * @property {string} description Details about the prerequisite
 * @property {string?} location Link which directs user to action location
 * @property {() => Promise<void> | void} action Function to be run when this prereq. task has been started
 */

const useNotificationsMessages = () => {
  const { user } = useAuth();
  const { t } = useTranslation('translation');

  /** @type {Prereq} */
  const USER_LOCATION = {
    displayName: t('finish-profile.user-location.title'),
    description: t('finish-profile.user-location.description'),
    location: PATH_DASHBOARD.settings.account('general', 'location'),
  };

  /** @type {Prereq} */
  const USER_VERIFY_PHONE = {
    displayName: t('finish-profile.verify-phone.title'),
    description: t('finish-profile.verify-phone.description'),
    location: PATH_DASHBOARD.settings.account('general', 'verify-phone'),
    skippable: true,
  };

  /** @type {Prereq} */
  const USER_PHONE = {
    displayName: t('finish-profile.set-phone-number.title'),
    description: t('finish-profile.set-phone-number.description'),
    location: PATH_DASHBOARD.settings.account('general', 'update-user'),
  };

  /** @type {Prereq} */
  const USER_REVERIFY_EMAIL = {
    displayName: t('finish-profile.reverify-email.title'),
    description: t('finish-profile.reverify-email.description'),
    location: PATH_DASHBOARD.reverifyEmailRequest,
  };

  /** @type {Prereq} */
  const INVITATIONS = {
    displayName: t('finish-profile.accept-team-invites.title'),
    description: t('finish-profile.accept-team-invites.description'),
    location: PATH_DASHBOARD.settings.account('invitations'),
  };

  /** @type {Prereq} */
  const RESCUE_HOURS = {
    displayName: t('finish-profile.set-organization-hours.title'),
    description: t('finish-profile.set-organization-hours.description'),
    location: PATH_DASHBOARD.settings.business('settings', 'hours'),
  };

  /** @type {Prereq} */
  const ORG_CAPACITY = {
    displayName: t('finish-profile.set-donation-capacity.title'),
    description: t('finish-profile.set-donation-capacity.description'),
    location: PATH_DASHBOARD.settings.business('settings', 'capacity'),
  };

  /** @type {Prereq} */
  const ORG_EIN = {
    displayName: isCanadaCountry(user.country)
      ? t('finish-profile.set-ein.title.ca')
      : t('finish-profile.set-ein.title.us'),
    description: t('finish-profile.set-ein.description'),
    location: PATH_DASHBOARD.settings.business('settings', 'tax-settings'),
  };

  /** @type {Prereq} */
  const TAX_CONTACT = {
    displayName: t('finish-profile.set-tax-contact.title'),
    description: t('finish-profile.set-tax-contact.description'),
    location: PATH_DASHBOARD.settings.business('settings', 'tax-settings'),
  };

  /** @type {Prereq} */
  const STRIPE_CONNECT = {
    displayName: t('finish-profile.stripe-connect.title'),
    description: t('finish-profile.stripe-connect.description'),
    location: PATH_DASHBOARD.settings.business('marketplace'),
  };

  /** @type {(enqueueSnackbar: Function) => Prereq} */
  const DESKTOP_NOTIFICATIONS = (enqueueSnackbar) => ({
    displayName: t('finish-profile.enable-desktop-notifications.title'),
    description: t('finish-profile.enable-desktop-notifications.description'),
    skippable: true,
    skipKey: 'desktop_notifications_skipped',
    action: () => {
      if (!window.Notification) return;
      if (Notification.permission === 'denied') {
        enqueueSnackbar(
          t(
            'finish-profile.notifications-blocked',
            'Notifications are blocked. Please enable them in your browser settings.'
          ),
          { variant: 'warning' }
        );
        return;
      }
      Notification.requestPermission((result) => {
        if (result === 'granted') {
          enqueueSnackbar(t('finish-profile.notifications-enabled'), {
            variant: 'info',
          });
        }
      });
    },
  });

  /** @type {Prereq} */
  const START_LOCATION_REQUIREMENT = {
    displayName: t('finish-profile.driver-start-location-requirement.title'),
    description:
      user.driver_start_address_required && user.is_driver
        ? t(
            'finish-profile.driver-start-location-requirement.description-required'
          )
        : t('finish-profile.driver-start-location-requirement.description'),
    location: PATH_DASHBOARD.settings.account('general'),
  };

  return {
    USER_LOCATION,
    USER_VERIFY_PHONE,
    USER_PHONE,
    USER_REVERIFY_EMAIL,
    INVITATIONS,
    RESCUE_HOURS,
    ORG_CAPACITY,
    DESKTOP_NOTIFICATIONS,
    START_LOCATION_REQUIREMENT,
    ORG_EIN,
    TAX_CONTACT,
    STRIPE_CONNECT,
  };
};

/**
 * Get a list of all tasks which need to be completed by a driver in order to
 * finish signing up.
 *
 * @returns {Prereq[]}
 */
export default function usePrereqs() {
  const { acceptedFoodTypes, invalidate } = useAcceptedFoodTypes();
  useEventBus('rescueSettings', 'update', invalidate);
  const { user, isFoodMoverFull } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const {
    DESKTOP_NOTIFICATIONS,
    INVITATIONS,
    ORG_CAPACITY,
    RESCUE_HOURS,
    USER_LOCATION,
    USER_PHONE,
    USER_VERIFY_PHONE,
    USER_REVERIFY_EMAIL,
    START_LOCATION_REQUIREMENT,
    ORG_EIN,
    TAX_CONTACT,
    STRIPE_CONNECT,
  } = useNotificationsMessages();
  const { invites } = useInvites(user.email);
  const { status: connectStatus } = useConnectOnboardingStatus();

  const prereqs = [];

  // skip super admins
  if (user.account_type === 'ADMIN') {
    return [];
  }

  if (user.is_non_profit || !(user.is_executive || user.is_corporate)) {
    // for npos and businesses
    const startLocation = parseJsonObject(user.start_location);
    if (user.is_non_profit && !startLocation.lat) {
      prereqs.push(START_LOCATION_REQUIREMENT);
    }
    if (!user.user_lat && user.is_driver) {
      prereqs.push(USER_LOCATION);
    }

    if (!user.phone) {
      prereqs.push(USER_PHONE);
    } else if (!user.is_phone_verified && !user.phone_verification_skipped) {
      prereqs.push(USER_VERIFY_PHONE);
    }

    if (!user.is_email_verified) {
      prereqs.push(USER_REVERIFY_EMAIL);
    }

    if (
      window.Notification &&
      Notification.permission !== 'granted' &&
      !localStorage.getItem('desktop_notifications_skipped')
    ) {
      prereqs.push(DESKTOP_NOTIFICATIONS(enqueueSnackbar));
    }

    if (invites.length) {
      prereqs.push(INVITATIONS);
    }

    if (user.is_admin) {
      if (user.is_non_profit) {
        let noHours =
          !Array.isArray(user.rescue_hours) ||
          !user.rescue_hours.find(
            ({ open_time, close_time }) => open_time || close_time
          );
        if (noHours) {
          prereqs.push(RESCUE_HOURS);
        }

        if (_isEmpty(acceptedFoodTypes)) {
          prereqs.push(ORG_CAPACITY);
        }
      }

      // Tax contact required for NPOs and Food Movers
      if (
        (user.is_non_profit || isFoodMoverFull) &&
        (!user.tax_contact_name || !user.tax_contact_email)
      ) {
        prereqs.push(TAX_CONTACT);
      }

      if (
        shouldShowOrgEinTask({
          ein: user.ein,
          country: user.country,
          isAdmin: user.is_admin,
          isNonProfit: user.is_non_profit,
          isFoodMover: isFoodMoverFull,
        })
      ) {
        prereqs.push(ORG_EIN);
      }

      // Stripe Connect onboarding required for marketplace-enabled orgs
      if (
        user.marketplace_enabled &&
        connectStatus &&
        connectStatus !== 'onboarded'
      ) {
        prereqs.push(STRIPE_CONNECT);
      }
    }
  }

  return prereqs;
}
